/**
 * MessagesView Component
 * Extracted from HubDashboard.jsx
 * Handles: Direct messages, conversations
 */

import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { User, Plus, Mail, RefreshCw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MessagesView() {
  const [conversations, setConversations] = useState([]);
  const [selectedConv, setSelectedConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [showNewChat, setShowNewChat] = useState(false);

  useEffect(() => {
    const fetchConversations = async () => {
      try {
        const res = await axios.get(`${API}/dm/conversations`, { withCredentials: true });
        setConversations(res.data);
        const usersRes = await axios.get(`${API}/dm/allowed-recipients`, { withCredentials: true });
        setUsers(usersRes.data);
      } catch (e) {
        console.error('Failed to load conversations');
      } finally {
        setLoading(false);
      }
    };
    fetchConversations();
  }, []);

  const loadMessages = async (convId) => {
    try {
      const res = await axios.get(`${API}/dm/conversations/${convId}/messages`, { withCredentials: true });
      setMessages(res.data);
      setConversations(prev => prev.map(c => c.conversation_id === convId ? {...c, unread_count: 0} : c));
    } catch (e) {
      console.error('Failed to load messages');
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedConv) return;
    try {
      const otherId = selectedConv.other_user_id;
      await axios.post(`${API}/dm/send`, {
        recipient_id: otherId,
        content: newMessage
      }, { withCredentials: true });
      setNewMessage('');
      loadMessages(selectedConv.conversation_id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send message');
    }
  };

  const startNewChat = async (userId) => {
    try {
      const res = await axios.get(`${API}/dm/user/${userId}`, { withCredentials: true });
      setShowNewChat(false);
      const convsRes = await axios.get(`${API}/dm/conversations`, { withCredentials: true });
      setConversations(convsRes.data);
      const newConv = convsRes.data.find(c => c.conversation_id === res.data.conversation_id);
      if (newConv) {
        setSelectedConv(newConv);
        loadMessages(newConv.conversation_id);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Cannot start chat with this user');
    }
  };

  if (loading) return <div className="flex justify-center py-12"><RefreshCw className="w-8 h-8 animate-spin text-accent" /></div>;

  return (
    <div className="grid grid-cols-3 gap-6 h-[600px]">
      {/* Conversations List */}
      <div className="bg-card/60 backdrop-blur-xl rounded-2xl border border-border/30 flex flex-col">
        <div className="p-4 border-b border-border/30 flex justify-between items-center">
          <h3 className="font-bold">Conversations</h3>
          <Button size="sm" onClick={() => setShowNewChat(true)}><Plus className="w-4 h-4" /></Button>
        </div>
        <div className="flex-1 overflow-y-auto">
          {conversations.length === 0 ? (
            <p className="text-center text-muted-foreground p-4">No conversations yet</p>
          ) : (
            conversations.map(conv => (
              <button
                key={conv.conversation_id}
                onClick={() => { setSelectedConv(conv); loadMessages(conv.conversation_id); }}
                className={`w-full p-4 text-left border-b border-border/20 hover:bg-accent/5 transition-colors ${selectedConv?.conversation_id === conv.conversation_id ? 'bg-accent/10' : ''}`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-accent/20 flex items-center justify-center">
                    <User className="w-5 h-5 text-accent" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{conv.other_user_name}</p>
                    <p className="text-xs text-muted-foreground truncate">{conv.last_message || 'No messages'}</p>
                  </div>
                  {conv.unread_count > 0 && (
                    <span className="w-5 h-5 bg-accent text-accent-foreground text-xs rounded-full flex items-center justify-center">{conv.unread_count}</span>
                  )}
                </div>
              </button>
            ))
          )}
        </div>
      </div>

      {/* Chat Area */}
      <div className="col-span-2 bg-card/60 backdrop-blur-xl rounded-2xl border border-border/30 flex flex-col">
        {selectedConv ? (
          <>
            <div className="p-4 border-b border-border/30">
              <h3 className="font-bold">{selectedConv.other_user_name}</h3>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {messages.map(msg => (
                <div key={msg.message_id} className={`flex ${msg.sender_id === selectedConv.other_user_id ? 'justify-start' : 'justify-end'}`}>
                  <div className={`max-w-[70%] px-4 py-2 rounded-2xl ${msg.sender_id === selectedConv.other_user_id ? 'bg-secondary' : 'bg-accent text-accent-foreground'}`}>
                    <p className="text-sm">{msg.content}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="p-4 border-t border-border/30 flex gap-2">
              <input
                type="text"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                placeholder="Type a message..."
                className="flex-1 px-4 py-2 rounded-xl bg-secondary border border-border/30 focus:outline-none focus:ring-2 focus:ring-accent"
              />
              <Button onClick={sendMessage}><Mail className="w-4 h-4" /></Button>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-muted-foreground">
            Select a conversation to start chatting
          </div>
        )}
      </div>

      {/* New Chat Modal */}
      {showNewChat && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center" onClick={() => setShowNewChat(false)}>
          <div className="bg-card p-6 rounded-2xl w-96 max-h-[500px] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <h3 className="font-bold text-lg mb-4">Start New Chat</h3>
            <p className="text-sm text-muted-foreground mb-4">Select a user you're allowed to message:</p>
            <div className="space-y-2">
              {users.length === 0 ? (
                <p className="text-center py-4 text-muted-foreground">No users available to message based on your role permissions.</p>
              ) : (
                users.map(u => (
                  <button
                    key={u.user_id}
                    onClick={() => startNewChat(u.user_id)}
                    className="w-full p-3 text-left rounded-xl hover:bg-accent/10 flex items-center gap-3"
                  >
                    <div className="w-10 h-10 rounded-full bg-accent/20 flex items-center justify-center overflow-hidden">
                      {u.picture ? (
                        <img src={u.picture} alt={u.name} className="w-full h-full object-cover" />
                      ) : (
                        <User className="w-5 h-5 text-accent" />
                      )}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium">{u.name}</p>
                      <p className="text-xs text-muted-foreground">{u.email}</p>
                    </div>
                    <span className="text-xs px-2 py-1 rounded-lg bg-secondary capitalize">{u.role_id === 'master_admin' ? 'Admin' : u.role_id?.replace('_', ' ')}</span>
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
